import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase, Database } from '../lib/supabase'
import { toast } from 'react-hot-toast'
import BulkUpload from '../components/BulkUpload'
import {
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

type PendingCookieCutter = Database['public']['Tables']['pending_cookie_cutters']['Row']
type CookieCutterCategory = Database['public']['Tables']['cookie_cutter_categories']['Row']
type StorageBin = Database['public']['Tables']['storage_bins']['Row']

interface PendingItem extends PendingCookieCutter {
  name?: string
  description?: string
  category?: string
  bin_id?: string
}

export default function BulkProcessing() {
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([])
  const [bins, setBins] = useState<StorageBin[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [editingItem, setEditingItem] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState<string | null>(null)
  
  const { user } = useAuth()

  const fetchPendingItems = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('pending_cookie_cutters')
        .select('*')
        .eq('user_id', user?.id)
        .in('status', ['uploaded', 'processing', 'failed'])
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching pending items:', error)
        toast.error('Failed to fetch pending items')
        return
      }

      const itemsWithFormData = (data || []).map((item: PendingCookieCutter) => ({
        ...item,
        name: item.suggested_name || '',
        description: '',
        category: item.suggested_category || '',
        bin_id: item.suggested_bin_id || '',
      }))

      setPendingItems(itemsWithFormData)
    } catch (error) {
      console.error('Error fetching pending items:', error)
      toast.error('Failed to fetch pending items')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const fetchStorageBins = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('storage_bins')
        .select('*')
        .eq('user_id', user?.id)
        .order('name')

      if (error) {
        console.error('Error fetching storage bins:', error)
        return
      }

      setBins(data || [])
    } catch (error) {
      console.error('Error fetching storage bins:', error)
    }
  }, [user?.id])

  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('cookie_cutter_categories')
        .select('name')
        .eq('user_id', user?.id)
        .order('name')

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching categories:', error)
        setCategories([
          'Holiday', 'Animals', 'Letters & Numbers', 'Basic Shapes',
          'Flowers', 'Food', 'Transportation', 'Sports', 'Other'
        ])
        return
      }

      const customCategories = data?.map((cat: CookieCutterCategory) => cat.name) || []
      const defaultCategories = [
        'Holiday', 'Animals', 'Letters & Numbers', 'Basic Shapes',
        'Flowers', 'Food', 'Transportation', 'Sports', 'Other'
      ]

      setCategories(customCategories.length > 0 ? customCategories : defaultCategories)
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([
        'Holiday', 'Animals', 'Letters & Numbers', 'Basic Shapes',
        'Flowers', 'Food', 'Transportation', 'Sports', 'Other'
      ])
    }
  }, [user?.id])

  useEffect(() => {
    if (user) {
      fetchPendingItems()
      fetchStorageBins()
      fetchCategories()
    }
  }, [user, fetchPendingItems, fetchStorageBins, fetchCategories])

  const updateItemField = (itemId: string, field: string, value: string) => {
    setPendingItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ))
  }

  const processItem = async (item: PendingItem) => {
    if (!item.name?.trim()) {
      toast.error('Please enter a name for the cookie cutter')
      return
    }

    if (!user) {
      toast.error('You must be logged in')
      return
    }

    try {
      // Create the cookie cutter
      const { error } = await supabase
        .from('cookie_cutters')
        .insert({
          name: item.name.trim(),
          description: item.description?.trim() || null,
          category: item.category || null,
          photo_url: item.photo_url,
          bin_id: item.bin_id || null,
          user_id: user.id
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating cookie cutter:', error)
        toast.error('Failed to process cookie cutter')
        return
      }

      // Mark pending item as completed
      await supabase
        .from('pending_cookie_cutters')
        .update({ 
          status: 'completed',
          processed_at: new Date().toISOString()
        })
        .eq('id', item.id)

      // Remove from local state
      setPendingItems(prev => prev.filter(p => p.id !== item.id))
      setSelectedItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(item.id)
        return newSet
      })

      toast.success('Cookie cutter processed successfully!')

    } catch (error) {
      console.error('Error processing item:', error)
      toast.error('Failed to process cookie cutter')
    }
  }

  const processSelectedItems = async () => {
    if (selectedItems.size === 0) {
      toast.error('No items selected')
      return
    }

    const itemsToProcess = pendingItems.filter(item => 
      selectedItems.has(item.id) && item.name?.trim()
    )

    if (itemsToProcess.length === 0) {
      toast.error('Selected items need names before processing')
      return
    }

    setProcessing(true)
    let successCount = 0
    let failCount = 0

    for (const item of itemsToProcess) {
      try {
        await processItem(item)
        successCount++
      } catch (error) {
        failCount++
      }
    }

    toast.success(
      `Processed ${successCount} items successfully${failCount > 0 ? `, ${failCount} failed` : ''}`
    )
    
    setProcessing(false)
    setSelectedItems(new Set())
  }

  const deleteItem = async (item: PendingItem) => {
    if (!window.confirm('Are you sure you want to delete this pending cookie cutter?')) {
      return
    }

    try {
      const { error } = await supabase
        .from('pending_cookie_cutters')
        .delete()
        .eq('id', item.id)

      if (error) {
        console.error('Error deleting pending item:', error)
        toast.error('Failed to delete item')
        return
      }

      setPendingItems(prev => prev.filter(p => p.id !== item.id))
      setSelectedItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(item.id)
        return newSet
      })

      toast.success('Item deleted successfully')
    } catch (error) {
      console.error('Error deleting item:', error)
      toast.error('Failed to delete item')
    }
  }

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectedItems.size === pendingItems.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(pendingItems.map(item => item.id)))
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-700">Loading pending items...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <CloudArrowUpIcon className="h-8 w-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">Bulk Processing</h1>
        </div>
        <p className="text-gray-700">
          Upload multiple photos at once and process them into your cookie cutter inventory. Add names, categories, and assign to storage bins.
        </p>
      </div>

      {/* Bulk Upload Section */}
      <div className="mb-8">
        <BulkUpload onComplete={() => {
          fetchPendingItems()
          toast.success('Photos uploaded! You can now process them below.')
        }} />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <CloudArrowUpIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-700">Total Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{pendingItems.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-700">Ready to Process</p>
              <p className="text-2xl font-semibold text-gray-900">
                {pendingItems.filter(item => item.name?.trim()).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <SparklesIcon className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-700">Selected</p>
              <p className="text-2xl font-semibold text-gray-900">{selectedItems.size}</p>
            </div>
          </div>
        </div>
      </div>

      {pendingItems.length === 0 ? (
        <div className="text-center py-12">
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No pending items</h3>
          <p className="mt-1 text-sm text-gray-700">
            Use bulk upload in the mobile app to add multiple photos at once.
          </p>
        </div>
      ) : (
        <>
          {/* Bulk Actions */}
          <div className="bg-white p-4 rounded-lg shadow border mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === pendingItems.length && pendingItems.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Select All ({selectedItems.size} selected)
                  </span>
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={processSelectedItems}
                  disabled={selectedItems.size === 0 || processing}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  {processing ? 'Processing...' : `Process Selected (${selectedItems.size})`}
                </button>
              </div>
            </div>
          </div>

          {/* Items Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {pendingItems.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow border overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2">
                {/* Image Header */}
                <div className="relative">
                  <img
                    src={item.photo_url}
                    alt="Cookie cutter"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={() => setShowPreview(item.id)}
                      className="bg-black bg-opacity-50 text-white p-1 rounded focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
                      aria-label="Preview image"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Status Badge */}
                  <div className="absolute bottom-2 left-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.status === 'uploaded' 
                        ? 'bg-blue-100 text-blue-800'
                        : item.status === 'processing'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.status === 'uploaded' ? 'Ready' : item.status}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="text-xs text-gray-700 mb-4">
                    Uploaded: {new Date(item.created_at).toLocaleString()}
                  </div>

                  {editingItem === item.id ? (
                    <div className="space-y-4">
                      {/* Name Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => updateItemField(item.id, 'name', e.target.value)}
                          placeholder="Enter cookie cutter name"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => updateItemField(item.id, 'description', e.target.value)}
                          placeholder="Optional description"
                          rows={2}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      {/* Category */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          value={item.category || ''}
                          onChange={(e) => updateItemField(item.id, 'category', e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">Select category (optional)</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>

                      {/* Storage Bin */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Storage Bin
                        </label>
                        <select
                          value={item.bin_id || ''}
                          onChange={(e) => updateItemField(item.id, 'bin_id', e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">No bin assigned</option>
                          {bins.map((bin) => (
                            <option key={bin.id} value={bin.id}>{bin.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Edit Actions */}
                      <div className="flex justify-between pt-4 border-t">
                        <button
                          onClick={() => setEditingItem(null)}
                          className="text-sm text-gray-700 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                        <div className="flex gap-2">
                          <button
                            onClick={() => deleteItem(item)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                          >
                            <TrashIcon className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                          <button
                            onClick={() => {
                              processItem(item)
                              setEditingItem(null)
                            }}
                            disabled={!item.name?.trim()}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Process
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {item.name || 'Unnamed Cookie Cutter'}
                        </h3>
                        {item.description && (
                          <p className="text-sm text-gray-700 mt-1">{item.description}</p>
                        )}
                      </div>

                      {(item.category || item.bin_id) && (
                        <div className="flex flex-wrap gap-2">
                          {item.category && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {item.category}
                            </span>
                          )}
                          {item.bin_id && bins.find(b => b.id === item.bin_id) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              📦 {bins.find(b => b.id === item.bin_id)?.name}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex justify-between pt-3 border-t">
                        <button
                          onClick={() => setEditingItem(item.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <PencilIcon className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => processItem(item)}
                          disabled={!item.name?.trim()}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Process
                        </button>
                      </div>
                    </div>
                  )}

                  {item.processing_error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      Error: {item.processing_error}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Image Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4" onClick={() => setShowPreview(null)}>
          <div className="relative max-w-4xl max-h-full">
            <img
              src={pendingItems.find(item => item.id === showPreview)?.photo_url}
              alt="Cookie cutter preview"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setShowPreview(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}